import { deleteLabOptions, getFullOrderOptions, type DeleteLabCommand, type LabDrawViewModel, type LabViewModel } from '@generated';
import { useAbbreviateFrequencies } from '../abbreviated/useAbbreviateFrequencies';
import { OrderItems } from '~/features/orders/OrderItems';
import { AddLabsModal } from './AddLabsFormModal';
import { useMutation } from '~/util/http/useMutation';
import { useGetLabOrders } from './queries';
import { useQuery } from '@tanstack/react-query';
import { Prompt } from '~/shared/components/Prompt';
import { SectionListRow } from '~/shared/section/section-list/SectionListRow';
import { EditLabFormModal } from './EditLabFormModal';
import { useFullOrder } from '~/features/orders/orders/FullOrderContext';

interface LabAndDraws {
	lab: LabViewModel;
	draws: LabDrawViewModel[];
}

export function LabOrders() {
	const { fullOrder } = useFullOrder();
	const deleteLab = useMutation<void, DeleteLabCommand>({
		...deleteLabOptions,
		invalidateQueryKeys: [getFullOrderOptions(fullOrder.id).queryKey]
	});
	const abbreviateFrequencies = useAbbreviateFrequencies();
	const { refetch } = useQuery(useGetLabOrders(fullOrder.id));

	const labs: LabAndDraws[] = fullOrder.labs
		.reduce((acc: LabAndDraws[], lab) => {
			const existingLab = acc.find(e => e.lab.id === lab.lab.id);
			if (existingLab) {
				existingLab.draws.push(lab);
			} else {
				acc.push({ lab: lab.lab, draws: [lab] });
			}
			return acc;
		}, [])
		.sort((a, b) => a.lab.labDefinition.name.localeCompare(b.lab.labDefinition.name));

	const onDeleteLab = (labId: string) => {
		deleteLab.mutate(
			{ orderId: fullOrder.id, labId },
			{
				onSuccess: () => {
					refetch();
				}
			}
		);
	};

	return (
		<OrderItems
			title="Labs"
			items={labs.map(lab => {
				const abbreviatedFrequencies = abbreviateFrequencies(lab.draws, fullOrder.certificationPeriod);
				const prn = fullOrder.prnLabDraws.find(x => x.labId === lab.lab.id);
				return (
					<SectionListRow
						key={lab.lab.id}
						label={
							<div className="flex flex-col gap-2">
								<div className="flex flex-row items-center gap-2">
									<span className="font-medium text-gray-900">{lab.lab.labDefinition.name}</span>
									{lab.draws.length > 0 && (
										<span className="ml-2 text-sm text-gray-500">
											{abbreviatedFrequencies
												.map(
													x =>
														`${
															x.abbreviationLabel
														} (${x.frequencyStartDate.toLocaleDateString()} - ${x.frequencyEndDate.toLocaleDateString()})`
												)
												.join(', ')}
										</span>
									)}
								</div>
							</div>
						}
						removeButtonProps={{
							label: 'Remove Lab',
							isPending: deleteLab.isPending && deleteLab.variables?.labId === lab.lab.id,
							renderModal: closeModal => (
								<Prompt
									title="Remove Lab"
									text="Are you sure you want to remove this lab?"
									onAction={() => {
										onDeleteLab(lab.lab.id);
										closeModal();
									}}
									onCancel={closeModal}
								/>
							)
						}}
						editButtonProps={{
							label: 'Edit Lab',
							renderModal: closeModal => <EditLabFormModal fullOrder={fullOrder} labId={lab.lab.id} closeModal={closeModal} />
						}}
					>
						<ul className="space-y-3">
							<ol>PRN: {prn ? 'Yes' : 'No'}</ol>
							<ol>Indication: {lab.lab.indication}</ol>
							{lab.lab.labCollectionTechniqueLabel && <ol>Collection Technique: {lab.lab.labCollectionTechniqueLabel}</ol>}
						</ul>
					</SectionListRow>
				);
			})}
			sectionButtonProps={{
				label: 'Add Lab',
				useSuspense: true,
				actionType: 'add',
				renderModal: closeModal => <AddLabsModal fullOrder={fullOrder} closeModal={closeModal} />
			}}
		/>
	);
}
